import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Target as TargetIcon, ImagePlus, X, Trash2, CalendarDays, MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DeleteButton } from "@/components/DeleteButton";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const proofUrl = (path) => {
  const t = localStorage.getItem("sc_token");
  return t && path ? `${BACKEND}/api/bd/photo/${path}?auth=${t}` : "";
};
const KRA_COLOR = {
  account_mapping: "#6366F1", specification: "#0EA5E9", engagement: "#E63946",
  product_positioning: "#D946EF", pipeline: "#F59E0B", channel: "#8B5CF6",
  display: "#14B8A6", reporting: "#64748B", dealer_training: "#F97316", architect_training: "#22C55E",
};
const kraColor = (key) => KRA_COLOR[key] || "#64748B";

function ScoreRing({ score }) {
  const r = 46, c = 2 * Math.PI * r, off = c - (Math.min(score, 100) / 100) * c;
  const color = score >= 80 ? "#10B981" : score >= 50 ? "#F59E0B" : "#E63946";
  return (
    <div className="relative h-32 w-32 shrink-0">
      <svg viewBox="0 0 110 110" className="h-32 w-32 -rotate-90">
        <circle cx="55" cy="55" r={r} fill="none" stroke="#eef2f7" strokeWidth="9" />
        <circle cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-3xl font-bold text-slate-900" data-testid="bd-overall-score">{score}</span>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">/ 100</span>
      </div>
    </div>
  );
}

function LogActivityDialog({ kras, open, onOpenChange, onSaved }) {
  const empty = { kra: "engagement", subtype: "", title: "", firm: "", location: "", touchpoints: "", value: "1", activity_date: new Date().toISOString().slice(0, 10), notes: "", photos: [] };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const kra = kras.find((x) => x.key === f.kra) || kras[0];

  const reset = () => setF(empty);
  const handleOpen = (v) => { if (v) reset(); onOpenChange(v); };

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/bd/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.storage_path);
      }
      setF((p) => ({ ...p, photos: [...p.photos, ...paths] }));
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!f.title.trim()) { toast.error("Add a short title"); return; }
    setSaving(true);
    try {
      await api.post("/bd/activities", { ...f, touchpoints: Number(f.touchpoints) || 0, value: Number(f.value) || 1 });
      toast.success("Activity logged");
      onSaved();
      handleOpen(false);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bd-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Log KRA Activity</DialogTitle>
          <DialogDescription className="sr-only">Record a business development activity with proof.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">KRA Category</Label>
              <Select value={f.kra} onValueChange={(v) => { set("kra", v); set("subtype", ""); }}>
                <SelectTrigger className="h-9" data-testid="bd-kra-select"><SelectValue /></SelectTrigger>
                <SelectContent>{kras.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Activity Type</Label>
              <Select value={f.subtype} onValueChange={(v) => set("subtype", v)}>
                <SelectTrigger className="h-9" data-testid="bd-subtype-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{kra.subtypes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Title *</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Lunch & Learn at ABC Architects" className="h-9" data-testid="bd-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Firm / Account</Label><Input value={f.firm} onChange={(e) => set("firm", e.target.value)} className="h-9" data-testid="bd-firm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Location</Label><Input value={f.location} onChange={(e) => set("location", e.target.value)} className="h-9" data-testid="bd-location" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">{kra.unit === "INR Lacs" ? "Value (INR Lacs)" : "Count"}</Label><Input type="number" value={f.value} onChange={(e) => set("value", e.target.value)} className="h-9" data-testid="bd-value" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Touchpoints</Label><Input type="number" value={f.touchpoints} onChange={(e) => set("touchpoints", e.target.value)} placeholder="0" className="h-9" data-testid="bd-touchpoints" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" value={f.activity_date} onChange={(e) => set("activity_date", e.target.value)} className="h-9" data-testid="bd-date" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} placeholder="Outcome, attendees, feedback…" data-testid="bd-notes" /></div>
          <div className="space-y-1.5">
            <Label className="text-xs">Proof Photos</Label>
            <div className="flex flex-wrap gap-2">
              {f.photos.map((p, i) => (
                <div key={p} className="relative h-14 w-14">
                  <img src={proofUrl(p)} alt="proof" className="h-14 w-14 rounded object-cover border border-slate-200" />
                  <button onClick={() => setF((s) => ({ ...s, photos: s.photos.filter((_, idx) => idx !== i) }))} className="absolute -top-1.5 -right-1.5 bg-white rounded-full border border-slate-200 text-slate-400 hover:text-red-500 p-0.5"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <label className="flex items-center justify-center h-14 w-14 rounded border border-dashed border-slate-300 text-slate-400 hover:text-brand hover:border-brand cursor-pointer">
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="bd-photo-input" />
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="bd-save-activity">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Log Activity</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BDTracker() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selectedBd, setSelectedBd] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: meta } = useQuery({ queryKey: ["bd-meta"], queryFn: async () => (await api.get("/bd/meta")).data });
  const isBd = user?.role === "bd";
  const canLog = meta?.can_log && (isBd || false);
  const reviewerHasUsers = !isBd && (meta?.bd_users?.length || 0) > 0;
  const viewUserId = isBd ? "" : (selectedBd || meta?.bd_users?.[0]?.id || "");

  const scoreParams = { year, month, ...(viewUserId ? { user_id: viewUserId } : {}) };
  const { data: scorecard, isLoading } = useQuery({
    queryKey: ["bd-scorecard", year, month, viewUserId],
    queryFn: async () => (await api.get("/bd/scorecard", { params: scoreParams })).data,
    enabled: !!meta && (isBd || !reviewerHasUsers || !!viewUserId),
  });
  const { data: activities = [] } = useQuery({
    queryKey: ["bd-activities", year, month, viewUserId],
    queryFn: async () => (await api.get("/bd/activities", { params: { year, month, ...(viewUserId ? { user_id: viewUserId } : {}) } })).data,
    enabled: !!meta && (isBd || !reviewerHasUsers || !!viewUserId),
  });

  const kras = meta?.kras || [];
  const rows = scorecard?.rows || [];
  const refresh = () => { qc.invalidateQueries({ queryKey: ["bd-scorecard"] }); qc.invalidateQueries({ queryKey: ["bd-activities"] }); };

  const del = async (id) => {
    try { await api.delete(`/bd/activities/${id}`); toast.success("Activity removed"); refresh(); }
    catch (e) { toast.error(apiError(e)); throw e; }
  };

  const selectedName = useMemo(() => {
    if (isBd) return user?.name;
    return meta?.bd_users?.find((u) => u.id === viewUserId)?.name || "—";
  }, [isBd, meta, viewUserId, user]);

  return (
    <div>
      <PageHeader
        title="BD KRA Tracker"
        subtitle={isBd ? `${user?.region || ""} Region · Update your KRAs and attach proof` : `Reviewing: ${selectedName}`}
      >
        {reviewerHasUsers && (
          <Select value={viewUserId} onValueChange={setSelectedBd}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="bd-person-select"><SelectValue placeholder="Select BD" /></SelectTrigger>
            <SelectContent>{meta.bd_users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.region}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="h-9 w-[110px]" data-testid="bd-month"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="h-9 w-[100px]" data-testid="bd-year"><SelectValue /></SelectTrigger>
          <SelectContent>{[now.getFullYear(), now.getFullYear() - 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        {canLog && <Button onClick={() => setDialogOpen(true)} className="gap-2 bg-brand hover:bg-brand/90" data-testid="bd-log-btn"><Plus className="h-4 w-4" /> Log Activity</Button>}
      </PageHeader>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <Card className="p-5 mb-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="text-center">
                <ScoreRing score={scorecard?.overall_score ?? 0} />
                <p className="text-xs text-slate-500 mt-2 font-medium">Monthly Scorecard</p>
              </div>
              <div className="flex-1 w-full grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {rows.map((r) => (
                  <div key={r.key} data-testid={`bd-kra-${r.key}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium text-slate-700">{r.num}. {r.label} {r.weight > 0 ? <span className="text-[10px] text-slate-400">· {r.weight}%</span> : <span className="text-[10px] text-slate-300">· tracking</span>}</span>
                      <span className="font-mono text-slate-500">{r.achieved}<span className="text-slate-300"> / {r.target} {r.unit}</span></span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${r.progress}%`, backgroundColor: kraColor(r.key), transition: "width 0.5s ease" }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <Card className="overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-sm text-slate-800">Activity Log · {MONTHS[month - 1]} {year}</h3>
              <span className="text-xs text-slate-400">{activities.length} activit{activities.length === 1 ? "y" : "ies"}</span>
            </div>
            {activities.length === 0 ? (
              <div className="py-14 text-center text-slate-400 text-sm" data-testid="bd-empty">No activities logged yet for this month.{canLog ? " Click \"Log Activity\" to start." : ""}</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {activities.map((a) => (
                  <div key={a.id} className="p-4 flex gap-4" data-testid={`bd-activity-${a.id}`}>
                    <div className="flex flex-wrap gap-1.5 shrink-0">
                      {(a.photos || []).slice(0, 3).map((p) => (
                        <a key={p} href={proofUrl(p)} target="_blank" rel="noreferrer"><img src={proofUrl(p)} alt="proof" className="h-14 w-14 rounded object-cover border border-slate-200" /></a>
                      ))}
                      {(!a.photos || a.photos.length === 0) && <div className="h-14 w-14 rounded bg-slate-50 border border-dashed border-slate-200" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px]" style={{ borderColor: kraColor(a.kra) + "55", color: kraColor(a.kra) }}>{a.kra_label}</Badge>
                        {a.subtype && <span className="text-xs text-slate-400">{a.subtype}</span>}
                      </div>
                      <p className="font-medium text-slate-800 mt-1">{a.title}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                        {a.firm && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{a.firm}</span>}
                        {a.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{a.location}</span>}
                        <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{a.activity_date}</span>
                        {!!a.touchpoints && <span>{a.touchpoints} touchpoints</span>}
                        <span className="font-mono">{a.kra === "pipeline" ? (Number(a.value || 0) / 100).toFixed(2) : `+${a.value}`}</span>
                      </div>
                      {a.notes && <p className="text-xs text-slate-400 mt-1.5">{a.notes}</p>}
                    </div>
                    {isBd && <DeleteButton testId={`bd-delete-${a.id}`} title="Remove this activity?" description="This deletes the logged activity and its proof references." onConfirm={() => del(a.id)} />}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      {canLog && <LogActivityDialog kras={kras} open={dialogOpen} onOpenChange={setDialogOpen} onSaved={refresh} />}
    </div>
  );
}
